import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { api } from '../../lib/api';
import { configQuery } from '../../lib/queries';
import type { ConfigEntry } from '../../lib/schemas';

const MCP_URL = `${import.meta.env.VITE_API_URL ?? ''}/mcp`;

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  function copy() {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="space-y-1">
      <p className="text-xs text-ndd-400">{label}</p>
      <div className="flex items-center gap-2 rounded-md border border-ndd-700 bg-ndd-900 px-3 py-2">
        <span className="flex-1 font-mono text-sm text-ndd-200 truncate">{value}</span>
        <button type="button" onClick={copy} className="shrink-0 text-ndd-400 hover:text-white">
          {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  );
}

function ConnectClaude() {
  return (
    <section>
      <h2 className="text-lg font-semibold mb-1">Connect to Claude</h2>
      <p className="text-sm text-ndd-400 mb-4">
        Add New Day Dairy as a custom connector at{' '}
        <a
          href="https://claude.ai/settings/connectors"
          target="_blank"
          rel="noopener noreferrer"
          className="text-ndd-300 underline hover:text-white"
        >
          claude.ai/settings/connectors
        </a>
        .
      </p>
      <div className="rounded-lg border border-ndd-800 p-4 space-y-4">
        <CopyField label="Name" value="New Day Dairy" />
        <CopyField label="Remote MCP Server URL" value={MCP_URL} />
        <p className="text-xs text-ndd-500">
          Leave OAuth Client ID and Client Secret blank — the server registers automatically.
        </p>
        <ol className="space-y-1 text-sm text-ndd-300 list-decimal list-inside">
          <li>Go to the link above and click the + button</li>
          <li>Paste the name and URL above</li>
          <li>Leave OAuth fields empty and click Add</li>
          <li>Click Connect and sign in with your Google account</li>
        </ol>
      </div>
    </section>
  );
}

export const Route = createFileRoute('/_auth/config')({
  beforeLoad: ({ context }) => {
    if (context.session.role !== 'farmer') throw redirect({ to: '/' });
  },
  component: ConfigPage,
});

// Parse user entries from config
function parseUsers(entries: ConfigEntry[]) {
  return entries
    .filter((e) => e.key.startsWith('user_') && e.key.endsWith('_role'))
    .map((e) => {
      const email = e.key.replace(/^user_/, '').replace(/_role$/, '');
      return { email, role: e.value, key: e.key };
    });
}

function parseAlertThresholds(entries: ConfigEntry[]) {
  return entries.filter((e) => e.key.startsWith('alert_'));
}

function ConfigPage() {
  const queryClient = useQueryClient();
  const { data } = useQuery(configQuery);
  const entries = data?.entries ?? [];
  const users = parseUsers(entries);
  const alertThresholds = parseAlertThresholds(entries);

  const [addUserOpen, setAddUserOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<'farmer' | 'helper' | 'viewer'>('helper');
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const addUserMutation = useMutation({
    mutationFn: ({ email, role }: { email: string; role: string }) =>
      api.post('/api/config', { key: `user_${email}_role`, value: role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configQuery.queryKey });
      setAddUserOpen(false);
      setNewEmail('');
      setNewRole('helper');
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (key: string) => api.delete(`/api/config/${encodeURIComponent(key)}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configQuery.queryKey });
      setDeleteTarget(null);
    },
  });

  const updateThresholdMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      api.post('/api/config', { key, value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configQuery.queryKey });
    },
  });

  return (
    <div className="space-y-8 max-w-2xl">
      <h1 className="text-2xl font-bold">Configuration</h1>

      {/* Users Section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Users</h2>
          <Button size="sm" onClick={() => setAddUserOpen(true)}>
            Add User
          </Button>
        </div>

        <div className="rounded-lg border border-ndd-800 overflow-hidden">
          {users.length === 0 ? (
            <p className="py-6 text-center text-ndd-400 text-sm">No users configured</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-ndd-800">
                <tr>
                  <th className="px-4 py-2 text-left text-ndd-400 font-medium">Email</th>
                  <th className="px-4 py-2 text-left text-ndd-400 font-medium">Role</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.key} className="border-b border-ndd-800 last:border-0">
                    <td className="px-4 py-3 text-white">{user.email}</td>
                    <td className="px-4 py-3 text-ndd-300 capitalize">{user.role}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteTarget(user.key)}
                        className="text-red-400 hover:text-red-300"
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Connect Claude Section */}
      <ConnectClaude />

      {/* Alert Thresholds Section */}
      <section>
        <h2 className="text-lg font-semibold mb-4">Alert Thresholds</h2>
        <div className="rounded-lg border border-ndd-800 overflow-hidden">
          {alertThresholds.length === 0 ? (
            <p className="py-6 text-center text-ndd-400 text-sm">No alert thresholds configured</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-ndd-800">
                <tr>
                  <th className="px-4 py-2 text-left text-ndd-400 font-medium">Key</th>
                  <th className="px-4 py-2 text-left text-ndd-400 font-medium">Description</th>
                  <th className="px-4 py-2 text-left text-ndd-400 font-medium">Value</th>
                </tr>
              </thead>
              <tbody>
                {alertThresholds.map((entry) => (
                  <ThresholdRow
                    key={entry.key}
                    entry={entry}
                    onSave={(value) => updateThresholdMutation.mutate({ key: entry.key, value })}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Add User Dialog */}
      <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="user@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={newRole} onValueChange={(v) => setNewRole(v as typeof newRole)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="farmer">Farmer</SelectItem>
                  <SelectItem value="helper">Helper</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddUserOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => addUserMutation.mutate({ email: newEmail, role: newRole })}
              disabled={!newEmail || addUserMutation.isPending}
            >
              Add User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove User</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-ndd-300">
            This will remove access for{' '}
            <span className="text-white font-semibold">
              {deleteTarget?.replace(/^user_/, '').replace(/_role$/, '')}
            </span>
            . They will no longer be able to sign in.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteTarget && deleteUserMutation.mutate(deleteTarget)}
              disabled={deleteUserMutation.isPending}
            >
              Remove User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ThresholdRow({ entry, onSave }: { entry: ConfigEntry; onSave: (value: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.value);

  function commit() {
    setEditing(false);
    onSave(draft);
  }

  return (
    <tr className="border-b border-ndd-800 last:border-0">
      <td className="px-4 py-3 font-mono text-xs text-ndd-400">{entry.key}</td>
      <td className="px-4 py-3 text-ndd-300 text-xs">{entry.description || '—'}</td>
      <td className="px-4 py-3">
        {editing ? (
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') {
                setEditing(false);
                setDraft(entry.value);
              }
            }}
            onBlur={commit}
            className="h-8 text-xs w-32"
            autoFocus
          />
        ) : (
          // biome-ignore lint/a11y/noStaticElementInteractions: inline edit, keyboard via input on activate
          // biome-ignore lint/a11y/useKeyWithClickEvents: inline edit, keyboard via input on activate
          <span
            onClick={() => setEditing(true)}
            className="cursor-text text-white hover:bg-ndd-800/40 rounded px-2 py-1"
          >
            {entry.value}
          </span>
        )}
      </td>
    </tr>
  );
}
